// product/ProductPage.jsx
// Route: /:storeSlug/:productSlug
// Fetches product from /api/marketplace/product/:storeSlug/:productSlug
// Assembles: ImageGallery + ProductInfo + PurchaseDrawer + SuccessScreen + RelatedProducts

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, AlertCircle } from "lucide-react";
import { MarketplaceHeader } from "../shared/MarketplaceHeader";
import { MarketplaceFooter } from "../shared/MarketplaceFooter";
import { WhatsAppFloat }     from "../shared/WhatsAppFloat";
import { ImageGallery }      from "./ImageGallery";
import { ProductInfo }       from "./ProductInfo";
import { PurchaseDrawer }    from "./PurchaseDrawer";
import { SuccessScreen }     from "./SuccessScreen";
import { RelatedProducts }   from "./RelatedProducts";
import { loadGoogleMapsAPI } from "../../../utils/loadGoogleMapsAPI";

const API_URL = process.env.REACT_APP_API_URL;

export default function ProductPage() {
  const { storeSlug, productSlug } = useParams();
  const navigate = useNavigate();

  const [product,           setProduct]           = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [notFound,          setNotFound]          = useState(false);
  const [drawerOpen,        setDrawerOpen]        = useState(false);
  const [googleMapsLoaded,  setGoogleMapsLoaded]  = useState(false);
  const [successData,       setSuccessData]       = useState(null); // { deliveryPointId }

  // ── Fetch product ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!storeSlug || !productSlug) return;
    setLoading(true);
    setNotFound(false);
    setProduct(null);

    fetch(`${API_URL}/api/marketplace/product/${storeSlug}/${productSlug}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then(data => { if (data) setProduct(data); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [storeSlug, productSlug]);

  // ── Load Google Maps once ────────────────────────────────────────────────
  useEffect(() => {
    loadGoogleMapsAPI()
      .then(() => setGoogleMapsLoaded(true))
      .catch(() => {});
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  function handleBuy()  { setDrawerOpen(true); }
  function handleClose(){ setDrawerOpen(false); }

  function handleSuccess({ deliveryPointId }) {
    setDrawerOpen(false);
    setSuccessData({ deliveryPointId });
  }

  function handleKeepShopping() {
    setSuccessData(null);
    navigate("/");
  }

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <MarketplaceHeader />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square rounded-2xl bg-slate-100 animate-pulse" />
          <div className="space-y-4 pt-4">
            <div className="h-4 w-1/3 rounded bg-slate-100 animate-pulse" />
            <div className="h-7 w-3/4 rounded bg-slate-200 animate-pulse" />
            <div className="h-8 w-1/4 rounded bg-slate-200 animate-pulse" />
            <div className="h-24 w-full rounded bg-slate-100 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );

  // ── Not found ────────────────────────────────────────────────────────────
  if (notFound) return (
    <div className="min-h-screen bg-slate-50">
      <MarketplaceHeader />
      <div className="flex flex-col items-center justify-center py-32 text-center px-4">
        <AlertCircle className="size-12 text-slate-300 mb-4" />
        <h1 className="text-xl font-extrabold text-slate-700 mb-2">Producto no encontrado</h1>
        <p className="text-sm text-slate-400 mb-6">
          Este producto no existe o fue eliminado por la tienda.
        </p>
        <Link
          to="/"
          className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
          style={{ background: "#B1336E" }}
        >
          Volver al marketplace
        </Link>
      </div>
    </div>
  );

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased text-slate-900 flex flex-col">
      <MarketplaceHeader />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="mx-auto max-w-5xl px-4 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className="size-3.5" />
            Volver
          </button>
        </div>

        {/* Product layout */}
        <div className="mx-auto max-w-5xl px-4 py-4 md:py-8">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-10">

            {/* Left: gallery */}
            <ImageGallery images={product?.images || []} />

            {/* Right: info + buy */}
            <ProductInfo
              product={product}
              onBuy={handleBuy}
            />
          </div>

          {/* Related products */}
          <RelatedProducts
            storeSlug={storeSlug}
            currentProductId={product?.id}
          />
        </div>
      </main>

      <MarketplaceFooter />
      <WhatsAppFloat />

      {/* Purchase drawer */}
      <PurchaseDrawer
        product={product}
        open={drawerOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
        googleMapsLoaded={googleMapsLoaded}
      />

      {/* Success overlay */}
      {successData && (
        <SuccessScreen
          deliveryPointId={successData.deliveryPointId}
          productTitle={product?.title}
          onKeepShopping={handleKeepShopping}
        />
      )}
    </div>
  );
}